import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import { getPublicFloor } from '../api/endpoints/floorPlan';
import { listMenuProducts } from '../api/endpoints/menu';
import { listOpenTabs, openTab } from '../api/endpoints/tabs';
import { useMutationAttempt } from '../lib/use-mutation-attempt';
import type { PublicFloorResource, TabRecord } from '../api/schemas';
import { EmptyState } from '../components/EmptyState';
import { ErrorBanner } from '../components/ErrorBanner';
import { FloorMap, type FloorMapMarker } from '../components/FloorMap';
import { LoadingState } from '../components/LoadingState';
import { PageHeader } from '../components/PageHeader';
import { TabEditor } from '../components/TabEditor';
import { usePermissions } from '../auth/usePermissions';
import { useSession } from '../auth/useSession';
import { formatMoneyCents } from '../lib/format';

export const TABS_QUERY_KEY = ['business-tabs'];
const MENU_QUERY_KEY = ['menu-products-active'];
/** The floor is shared work: another waiter's plato should appear here. */
const TABS_REFETCH_INTERVAL_MS = 15_000;
/** Architecture, not a seat: a bar counter never carries a comanda. */
const UNSERVED_KIND = 'bar';

type FloorMarker = FloorMapMarker & { resource: PublicFloorResource };

/**
 * Comandas: the floor as the counter works it. Every mesa with an open cuenta
 * carries its running total; tapping an empty one opens a comanda on it.
 *
 * Occupancy state rides into `FloorMap` through `markerDataAttrs` and the total
 * through `renderMarkerExtra`. The floor read is the PUBLIC one, so an employee
 * never needs owner-only table access to work a shift.
 */
export function ComandasPage() {
  const { session } = useSession();
  const drawsFloor = usePermissions().can('servicios');
  const queryClient = useQueryClient();
  const [openTabId, setOpenTabId] = useState<string | undefined>();

  const floorQuery = useQuery({
    queryKey: ['public-floor', session?.businessId],
    queryFn: () => getPublicFloor(session!.businessId),
    enabled: Boolean(session?.businessId),
  });
  const tabsQuery = useQuery({
    queryKey: TABS_QUERY_KEY,
    queryFn: listOpenTabs,
    refetchInterval: TABS_REFETCH_INTERVAL_MS,
  });
  const menuQuery = useQuery({ queryKey: MENU_QUERY_KEY, queryFn: () => listMenuProducts(false) });

  const tabs = useMemo(() => tabsQuery.data?.data ?? [], [tabsQuery.data]);
  const byTable = useMemo(() => {
    const map = new Map<string, TabRecord>();
    for (const tab of tabs) map.set(tab.tableId, tab);
    return map;
  }, [tabs]);
  // A settled tab leaves the open list, so hold the one on screen separately
  // rather than losing the receipt the moment the last guest pays.
  const [settledTab, setSettledTab] = useState<TabRecord | undefined>();
  const shown = tabs.find((tab) => tab.id === openTabId) ?? (settledTab?.id === openTabId ? settledTab : undefined);

  const markers: FloorMarker[] = useMemo(
    () =>
      (floorQuery.data?.resources ?? []).map((resource) => ({
        id: resource.id,
        name: resource.name,
        seats: resource.seats,
        kind: resource.kind,
        gridX: resource.x,
        gridY: resource.y,
        gridW: resource.w,
        gridH: resource.h,
        shape: resource.shape,
        rotation: resource.rotation,
        isPrivate: resource.isPrivate,
        resource,
      })),
    [floorQuery.data],
  );

  // The server answers every write with the whole tab, so the cache is written
  // straight through: an invalidate here would refetch across Hyperdrive's
  // stale window and un-draw the plato that was just rung up.
  const writeTab = (tab: TabRecord) => {
    setSettledTab(tab.status === 'settled' ? tab : undefined);
    queryClient.setQueryData<{ data: TabRecord[] }>(TABS_QUERY_KEY, (old) => {
      const rest = (old?.data ?? []).filter((entry) => entry.id !== tab.id);
      return { data: tab.status === 'open' ? [...rest, tab] : rest };
    });
  };

  const openMutation = useMutationAttempt({
    mutationFn: openTab,
    onSuccess: (tab) => {
      writeTab(tab);
      setOpenTabId(tab.id);
    },
  });

  function markerTap(marker: FloorMarker) {
    if (openMutation.isPending || openMutation.uncertain) return;
    if (marker.kind === UNSERVED_KIND) return;
    const existing = byTable.get(marker.id);
    if (existing) {
      setOpenTabId(existing.id);
      return;
    }
    openMutation.mutate(marker.id);
  }

  const loading = floorQuery.isLoading || tabsQuery.isLoading;

  return (
    <main>
      <PageHeader title="Comandas" description="Las cuentas abiertas en el salón." />
      {loading ? <LoadingState label="Cargando el salón…" /> : null}
      {floorQuery.isError ? <ErrorBanner message="No pudimos cargar el plano." /> : null}
      {tabsQuery.isError ? <ErrorBanner message={errorMessage(tabsQuery.error)} /> : null}
      {openMutation.isError ? <ErrorBanner message={errorMessage(openMutation.error)} /> : null}
      {openMutation.uncertain ? <button type="button" className="btn secondary" disabled={openMutation.isPending} onClick={openMutation.retry}>Reintentar abrir cuenta</button> : null}

      {shown ? (
        <TabEditor
          key={shown.id}
          tab={shown}
          tableName={markers.find((marker) => marker.id === shown.tableId)?.name ?? 'La mesa'}
          products={menuQuery.data?.data ?? []}
          {...(menuQuery.isError ? { productsError: errorMessage(menuQuery.error) } : {})}
          onTabChange={writeTab}
          onClose={() => setOpenTabId(undefined)}
        />
      ) : (
        <section className="section comandas" aria-label="Plano">
          <FloorMap
            plan={floorQuery.data?.floorPlan ?? null}
            markers={markers}
            emptyState={
              markers.length === 0 && !loading ? (
                <div className="plan-empty">
                  <EmptyState
                    title="Todavía no hay mesas."
                    description={
                      drawsFloor
                        ? 'Dibuja el plano en Mesas para empezar a tomar comandas.'
                        : 'Pídele a la persona dueña que dibuje el plano para empezar a tomar comandas.'
                    }
                  />
                </div>
              ) : undefined
            }
            markerDataAttrs={(marker) => {
              const tab = byTable.get(marker.id);
              return {
                disabled: marker.kind !== UNSERVED_KIND && (openMutation.isPending || openMutation.uncertain),
                'data-private': marker.resource.isPrivate || undefined,
                'data-occupied': tab ? true : undefined,
                // FloorMap owns the marker's aria-label, so the state that is
                // only drawn (the copper wash and the running total) rides in
                // here rather than staying invisible to a screen reader.
                ...(tab
                  ? { 'aria-description': `Cuenta abierta, ${formatMoneyCents(tab.totalCents)}` }
                  : {}),
              };
            }}
            renderMarkerExtra={(marker) => {
              const tab = byTable.get(marker.id);
              if (!tab) return null;
              return <span className="plan-occupant">{formatMoneyCents(tab.totalCents)}</span>;
            }}
            isMarkerInteractive={(marker) => marker.kind !== UNSERVED_KIND}
            onMarkerActivate={markerTap}
          />
          {markers.length > 0 ? (
            <p className="row-sub plan-hint" aria-live="polite">
              {openMutation.isPending
                ? 'Abriendo la cuenta…'
                : openSummary(tabs)}
            </p>
          ) : null}
        </section>
      )}
    </main>
  );
}

/** What the floor is carrying right now, said once for everyone. */
function openSummary(tabs: TabRecord[]): string {
  if (tabs.length === 0) return 'Toca una mesa para abrir su cuenta.';
  const total = tabs.reduce((sum, tab) => sum + tab.totalCents, 0);
  const mesas = tabs.length === 1 ? '1 mesa con cuenta abierta' : `${tabs.length} mesas con cuenta abierta`;
  return `${mesas} · ${formatMoneyCents(total)}. Toca una para seguirla.`;
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
